import { useState, useCallback } from "react";
import Input from "components/ui/Input";
import ItemSelectSizeGrouped from "components/item/ItemSelectSizeGrouped";
import ItemSizesTable from "./ItemSizesTable";
import { useOrderHeader } from "hooks/order";
import useOrderStore from "store/order";
import { useItemDetailsWithSizes } from "http-hooks/agitem/useAgitem";

// QuantityCell component
const QuantityCell = ({ column, rowIndex }) => {
  // Internal editing state
  const [isEditing, setIsEditing] = useState(false);
  
  // Get columnOrderItems, setColumnOrderItems, and sizeColumns from the hook
  const { columnOrderItems, setColumnOrderItems, sizeToColumnMap } = useOrderHeader();
   
  // Internal updateQuantity function
  const updateQuantity = useCallback((rowIndex, columnIndex, value) => {
    setColumnOrderItems(columnOrderItems.map((row, index) => {
      if (index === rowIndex) {
        // Find existing quantity entry or create new one
        const newQuantities = [...row.quantities];
        const existingIndex = newQuantities.findIndex(q => q.col === columnIndex);
        
        // Convert value to float and check if it's valid
        const floatValue = parseFloat(value);
        const isValidValue = !isNaN(floatValue) && floatValue > 0;
        
        if (existingIndex >= 0) {
          if (isValidValue) {
            newQuantities[existingIndex] = { ...newQuantities[existingIndex], qty: floatValue.toString() };
          } else {
            newQuantities.splice(existingIndex, 1); // Remove if empty, null, or 0
          }
        } else if (isValidValue) {
          // Find matching size data from row.itemSizes based on column index
          // First, find which size corresponds to this column by checking sizeToColumnMap
          let matchingSizeData = null;
          
          if (row.itemSizes && row.itemSizes.length > 0) {
            // Look through itemSizes to find one that maps to this column index
            matchingSizeData = row.itemSizes.find(sizeInfo => {
              const columnId = `${sizeInfo.sz}_${sizeInfo.szuom}`;
              return sizeToColumnMap.get(columnId) === columnIndex;
            });
          }
          
          // Create complete quantity entry with actual item size information
          const newQuantity = {
            col: columnIndex,
            qty: floatValue.toString()
          };
          
          // Add size information if available from itemSizes
          if (matchingSizeData) {
            newQuantity.sz = matchingSizeData.sz;
            newQuantity.szuom = matchingSizeData.szuom;
            newQuantity.itmsztyp = matchingSizeData.itmsztyp;
            newQuantity.packratio = matchingSizeData.packratio;
            newQuantity.agitemdtlId = matchingSizeData.agitemdtlId;
          }
          
          newQuantities.push(newQuantity);
        }

        return {
          ...row,
          quantities: newQuantities
        };
      }
      return row;
    }));
  }, [columnOrderItems, setColumnOrderItems, sizeToColumnMap]);
  
  // Get the specific row using rowIndex
  const currentRow = columnOrderItems[rowIndex];
  
  // Find quantity that matches this column.index directly
  const quantity = currentRow?.quantities.find(q => q.col === column.index);
  
  const value = quantity?.qty || '';

  const handleEdit = () => setIsEditing(true);
  const handleBlur = () => setIsEditing(false);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
    }
  };

  return (
    <td 
      key={column.id} 
      className="px-1 py-1 text-center cursor-pointer border border-gray-300"
      style={{ minWidth: '40px', width: '40px' }}
      onDoubleClick={handleEdit}
    >
      {isEditing ? (
        <Input
          type="number"
          showLabel={false}
          value={value}
          onChange={(e) => updateQuantity(rowIndex, column.index, e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="[&_input]:text-center [&_input]:py-1 [&_input]:px-1 [&_input]:text-xs"
          placeholder="0"
          autoFocus
          step="0.00001"
        />
      ) : (
        <div className="min-h-[20px] flex items-center justify-center">
          {value && parseFloat(value) > 0 ? (
            <span className="text-gray-900 font-bold text-xs">{value}</span>
          ) : (
            <span className="text-gray-400 text-xs">0</span>
          )}
        </div>
      )}
    </td>
  );
};

export default function SetColumnOrderDetails() {
  const { 
    orderHeader, 
    columnOrderItems, 
    setColumnOrderItems, 
    sizeColumns, 
    sizeToColumnMap 
  } = useOrderHeader();
  const [activeRowId, setActiveRowId] = useState(null);

  // Mutation hook to fetch item sizes
  const { mutateAsync: fetchItemSizes } = useItemDetailsWithSizes();

  // Calculate total for a row on the fly
  const calculateRowTotal = useCallback((row) => {
    return row.quantities.reduce((sum, q) => sum + (parseFloat(q.qty) || 0), 0);
  }, []);

  const createEmptyRow = useCallback((caseId = 1) => {
    return {
      caseId,
      item: null,
      quantities: [], // Unified array: {sz, szuom, itmsztyp, packratio, agitemdtlId, qty, col}
      itemSizes: [] // Array to store fetched item size data
    };
  }, []);

  const addNewRow = useCallback((onlyIfLastRowHasItem = false, targetRowIndex = null) => {
    // If we need to check conditions, get current state from store
    const currentItems = onlyIfLastRowHasItem || targetRowIndex !== null 
      ? useOrderStore.getState().columnOrderItems 
      : columnOrderItems;

    // Check if we should only add when last row has item
    if (onlyIfLastRowHasItem && targetRowIndex !== null) {
      const isLastRow = targetRowIndex === currentItems.length - 1;
      const hasItem = currentItems[targetRowIndex]?.item;
      
      if (!isLastRow || !hasItem) {
        return; // Don't add row
      }
    }

    const lastRow = currentItems[currentItems.length - 1];
    const caseId = lastRow ? lastRow.caseId : 1; // Always copy the last case ID

    const newRow = createEmptyRow(caseId);
    if (newRow) {
      setColumnOrderItems([...currentItems, newRow]);
    }
  }, [createEmptyRow, columnOrderItems, setColumnOrderItems]);

  const removeRow = useCallback((rowIndex) => {
    setColumnOrderItems(columnOrderItems.filter((row, index) => index !== rowIndex));
  }, [columnOrderItems, setColumnOrderItems]);

  const updateRowItem = useCallback(async (rowIndex, selectedItem) => {
    if (selectedItem) {
      setActiveRowId(rowIndex);
      
      // Fetch item sizes using the mutation
      const itemSizes = await fetchItemSizes(selectedItem.value);
      
      // Single update with both item and fetched sizes
      setColumnOrderItems(columnOrderItems.map((row, index) => {
        if (index === rowIndex) {
          return { 
            ...row, 
            item: selectedItem,
            quantities: [], // Reset quantities
            itemSizes: itemSizes || [] // Set fetched item sizes
          };
        }
        return row;
      }));
      
      // Add new row if needed after state has updated (auto-increment case ID)
      setTimeout(() => {
        addNewRow(true, rowIndex);
      }, 1000);
    } else {
      // If item is cleared, hide sizes table and clear row
      setActiveRowId(null);
      setColumnOrderItems(columnOrderItems.map((row, index) => {
        if (index === rowIndex) {
          return { 
            ...row, 
            item: selectedItem,
            quantities: [], // Reset quantities
            itemSizes: [] // Reset item sizes
          };
        }
        return row;
      }));
    }
  }, [columnOrderItems, setColumnOrderItems, addNewRow, fetchItemSizes]);

  console.log({columnOrderItems});

  const updateCaseId = useCallback((rowIndex, caseId) => {
    setColumnOrderItems(columnOrderItems.map((row, index) => {
      if (index === rowIndex) {
        return { ...row, caseId: parseInt(caseId) || 1 };
      }
      return row;
    }));
  }, [columnOrderItems, setColumnOrderItems]);

  // Get case info for individual case display in table (similar to rate order)
  const getCaseInfo = useCallback((caseId) => {
    // Find all rows with the same case ID
    const caseRows = columnOrderItems.filter(row => row.caseId === caseId && row.item);
    
    if (caseRows.length === 0) return null;

    // Calculate total pack ratio and quantity for all rows in this case
    let totalPack = 0;
    let totalQuantity = 0;

    caseRows.forEach(row => {
      row.quantities.forEach(q => {
        const quantity = parseFloat(q.qty) || 0;
        totalQuantity += quantity;
        
        // Only calculate pack ratio if packratio exists (from ItemSizesTable)
        if (q.packratio) {
          const packRatio = parseFloat(q.packratio) || 0;
          totalPack += packRatio * quantity;
        }
      });
    });

    if (totalQuantity === 0) return null;

    // If no pack ratio data, just show total quantity without percentage
    if (totalPack === 0) {
      return {
        percentage: null,
        totalQuantity,
        isEfficient: null,
        color: 'text-gray-600',
        bgClass: 'bg-gray-50'
      };
    }

    const percentage = Math.round(totalPack);
    const isEfficient = percentage >= 95 && percentage <= 105;
    
    return {
      percentage,
      totalQuantity,
      isEfficient,
      color: isEfficient ? 'text-green-600' : 'text-red-600',
      bgClass: isEfficient ? 'bg-green-50' : 'bg-red-50'
    };
  }, [columnOrderItems]);

  // Show empty state if no size columns
  if (!sizeColumns.length) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 mt-8">
        <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-8">
          <div className="text-center">
            <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-600 font-medium">No size columns configured</p>
            <p className="text-gray-500 text-sm mt-2">Please configure size groups in the system first</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-8">
      {/* Hotkey hint */}
      <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
        <span>Press</span>
        <kbd className="inline-flex items-center rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-600 shadow-sm">]</kbd>
        <span>to open the Lot details modal</span>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-800 to-blue-700 px-6 py-3">
          <h2 className="text-lg font-bold text-white">Column Order Items</h2>
          <p className="text-gray-300 text-xs mt-0.5">
            {orderHeader.ordvendid ? 'Vendor selected - items available' : 'Select vendor to load items'}
          </p>
        </div>

        <div className="overflow-x-auto" style={{ height: '800px' }}>
          <table className="min-w-full border-collapse border border-gray-300" style={{ tableLayout: 'fixed' }}>
            <thead>
              {/* First row - Case and Item columns + empty header */}
              <tr>
                <th 
                  rowSpan="4" 
                  className="bg-gray-50 border border-gray-300 py-2 px-1 text-center text-xs font-bold text-gray-900 uppercase tracking-wider"
                  style={{ minWidth: '40px', width: '40px' }}
                >
                  CS
                </th>
                <th 
                  rowSpan="4" 
                  className="bg-gray-50 border border-gray-300 py-2 px-3 text-center text-xs font-bold text-gray-900 uppercase tracking-wider"
                  style={{ minWidth: '280px', width: '280px' }}
                >
                  Item name
                  <button
                    type="button"
                    onClick={() => addNewRow()}
                    className="ml-2 inline-flex items-center text-blue-600 hover:text-blue-800"
                    title="Add new item"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </th>
              </tr>

              {/* Second row - CM sizes */}
              <tr style={{ background: '#d4e0f7' }}>
                {sizeColumns.map(column => (
                  <th 
                    key={`cm-${column.id}`} 
                    className="bg-blue-100 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                    style={{ minWidth: '40px', width: '40px' }}
                  >
                    {column.sizes.CM ? column.sizes.CM.itm_sz : ''}
                  </th>
                ))}
                <th 
                  className="bg-blue-100 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                  style={{ minWidth: '60px', width: '60px' }}
                >
                </th>
                <th 
                  rowSpan="1" 
                  className="bg-blue-100 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                  style={{ minWidth: '40px', width: '40px' }}
                >
                </th>
              </tr>

              {/* Third row - INCH sizes */}
              <tr style={{ background: '#cff3f9' }}>
                {sizeColumns.map(column => (
                  <th 
                    key={`inch-${column.id}`} 
                    className="bg-cyan-100 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                    style={{ minWidth: '40px', width: '40px' }}
                  >
                    {column.sizes.INCH ? column.sizes.INCH.itm_sz : ''}
                  </th>
                ))}
                <th 
                  className="bg-cyan-100 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                  style={{ minWidth: '60px', width: '60px' }}
                >
                </th>
                <th 
                  className="bg-cyan-100 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                  style={{ minWidth: '40px', width: '40px' }}
                >
                  Row
                </th>
              </tr>

              {/* Fourth row - Roman sizes */}
              <tr style={{ background: '#e0f4ff' }}>
                {sizeColumns.map(column => (
                  <th 
                    key={`roman-${column.id}`} 
                    className="bg-blue-50 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                    style={{ minWidth: '40px', width: '40px' }}
                  >
                    {column.sizes.Roman ? column.sizes.Roman.itm_sz : ''}
                  </th>
                ))}
                <th 
                  className="bg-blue-50 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                  style={{ minWidth: '60px', width: '60px' }}
                >
                  Case Info
                </th>
                <th 
                  className="bg-blue-50 border border-gray-300 py-1 px-1 text-center text-xs font-bold text-gray-900"
                  style={{ minWidth: '40px', width: '40px' }}
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {columnOrderItems.length === 0 ? (
                <tr>
                  <td colSpan={sizeColumns.length + 4} className="p-8 text-center">
                    <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <p className="text-gray-600 font-medium">No items added yet</p>
                    <p className="text-gray-500 text-sm mt-2">Click "Add Row" to start adding items to your order</p>
                    <button
                      type="button"
                      onClick={() => addNewRow()}
                      disabled={!orderHeader.ordvendid || !sizeColumns.length}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add First Row
                    </button>
                  </td>
                </tr>
              ) : (
                columnOrderItems.map((row, index) => (
                  <tr key={`row-${index}`} className="hover:bg-gray-50 transition-colors duration-150">
                    {/* Case ID */}
                    <td className="bg-white border border-gray-300 py-2 px-1" style={{ width: '40px' }}>
                      <Input
                        type="number"
                        showLabel={false}
                        value={row.caseId}
                        onChange={(e) => updateCaseId(index, e.target.value)}
                        className="w-[25px] [&_input]:text-right [&_input]:py-1 [&_input]:px-1 [&_input]:text-xs"
                      />
                    </td>

                    {/* Item Selection */}
                    <td className="bg-white border border-gray-300 py-2 px-1" style={{ width: '280px' }}>
                      <div>
                        <ItemSelectSizeGrouped
                          value={row.item}
                          onChange={(selectedItem) => updateRowItem(index, selectedItem)}
                          vendorId={orderHeader.ordvendid}
                          placeholder="Select item..."
                          isDisabled={!orderHeader.ordvendid}
                        />
                        {activeRowId === index && row.item && (
                          <div>
                            {/* Loading state when fetching item sizes */}
                            {!row.itemSizes || row.itemSizes.length === 0 ? (
                              <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded">
                                <div className="flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                  <span className="text-sm text-gray-600">Loading sizes...</span>
                                </div>
                              </div>
                            ) : (
                              <ItemSizesTable 
                                row={row} 
                                rowIndex={index}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Quantity Columns */}
                    {sizeColumns.map((column, columnIndex) => (
                      <QuantityCell
                        key={column.id}
                        column={column}
                        rowIndex={index}
                      />
                    ))}

                    {/* Case Info */}
                    <td className="border border-gray-300 py-1 px-1 text-center" style={{ width: '60px' }}>
                      {(() => {
                        const caseInfo = getCaseInfo(row.caseId);
                        return caseInfo ? (
                          <div className={`${caseInfo.bgClass} rounded-md p-1 text-xs text-center space-y-0.5 border border-gray-200`}>
                            <div className={`font-bold text-xs ${caseInfo.color}`}>
                              {caseInfo.percentage ? caseInfo.percentage + '%' : '-'}
                            </div>
                            <div className="text-gray-600 font-medium text-xs">
                              {caseInfo.totalQuantity.toFixed(2)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-400 text-xs">-</div>
                        );
                      })()}
                    </td>

                    {/* Actions and Total */}
                    <td className="border border-gray-300 py-1 px-1 text-center" style={{ width: '40px' }}>
                      <div className="flex flex-col items-center space-y-1">
                        <button
                          type="button"
                          onClick={() => addNewRow()}
                          className="inline-flex items-center text-green-600 hover:text-green-800"
                          title="Add new row"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          className="inline-flex items-center text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                          title="Remove row"
                        >
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <Input
                          type="text"
                          showLabel={false}
                          value={calculateRowTotal(row).toFixed(2)}
                          readOnly
                          className="[&_input]:text-center [&_input]:py-1 [&_input]:px-1 [&_input]:text-xs [&_input]:font-bold [&_input]:bg-gray-100"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 